"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.orderLeaderboardData = void 0;
const courseDataHelpers_1 = require("../Utils/courseDataHelpers");
const stablefordUtils_1 = require("./stablefordUtils");
const seriesPointsDebug_1 = require("./seriesPointsDebug");
const orderLeaderboardData = ({ roundNumber, localRoundValue, leaderboardDataParam, leaderboardData, courseDataParam, leaderboardCourseData, eventData, leaderboardScoreFormat }) => {
    let orderRound = localRoundValue || 1;
    if (roundNumber) {
        orderRound = roundNumber;
    }
    const leaderboardDataToUse = leaderboardDataParam || leaderboardData;
    const courseDataToUse = courseDataParam || leaderboardCourseData;
    const roundPlayType = eventData?.rounds?.[orderRound - 1]?.playType;
    const stableford = (0, stablefordUtils_1.isStablefordPlayType)(roundPlayType);
    const holePoints = eventData?.rounds?.[orderRound - 1]?.holePoints ?? [];
    const isNet = leaderboardScoreFormat === 'net';
    // console.log('Ordering leaderboard data. I have leaderboardData of:', leaderboardDataToUse.length);
    // console.log('Leaderboard data:', leaderboardDataToUse);
    // console.log('Course data:', courseDataToUse);
    const processedLeaderboardData = leaderboardDataToUse.map((item) => {
        let totalScore = 0;
        let totalScoreToPar = 0;
        let totalPoints = 0;
        let holesCompleted = 0;
        // Get the courseID for the current round
        const roundCourseID = eventData?.rounds?.[orderRound - 1]?.courseID;
        // Find the correct course from the array
        const currentCourseData = (0, courseDataHelpers_1.getCourseById)(courseDataToUse, roundCourseID);
        const teeSetData = (0, courseDataHelpers_1.getTeeSetFromCourse)(currentCourseData, item?.playerInfo?.[0]?.teeSet);
        const round = item?.roundScores?.[orderRound - 1];
        const holeScores = round?.holeScores || [];
        holeScores.forEach((holeScore) => {
            if (holeScore.playerScore !== 0 && holeScore.playerScore !== null) {
                const adjustedScore = holeScore.playerScore - (isNet ? holeScore.playerHcpAdj : 0);
                totalScore += adjustedScore;
                totalScoreToPar += adjustedScore - (teeSetData?.holes?.[holeScore.holeNumber - 1]?.par || 0);
                if (stableford) {
                    const holePts = (0, stablefordUtils_1.calculateHoleStablefordPoints)(holeScore.playerScore, holeScore.par, holeScore.playerHcpAdj ?? 0, isNet, holePoints);
                    totalPoints += holePts;
                }
                holesCompleted++;
            }
        });
        if (round?.widthdrawn) {
            totalScore = 1000;
            totalScoreToPar = 1000;
            totalPoints = -1000;
        }
        if (round?.disqualified) {
            totalScore = 1000;
            totalScoreToPar = 1000;
            totalPoints = -1000;
        }
        return {
            _id: item._id,
            playerInfo: item.playerInfo,
            playerName: (item.playerInfo ?? []).map((player) => player.playerName).join(' / '),
            teamName: item.playerInfo?.[0]?.teamName,
            rivalryTeam: item.playerInfo?.[0]?.rivalryTeam,
            groupNumber: item.playerInfo?.[0]?.groupNumber,
            roundNumber: orderRound,
            totalScore: totalScore,
            toPar: totalScoreToPar,
            totalPoints: stableford ? totalPoints : undefined,
            holesCompleted: holesCompleted,
            roundScores: item.roundScores,
            widthdrawn: round?.widthdrawn,
            disqualified: round?.disqualified,
            indPlayoffWin: item.indPlayoffWin,
            teamPlayoffWin: item.teamPlayoffWin,
            // adminRank holds the organiser-set finalRank value and is never
            // overwritten by the ranking algorithm, so callers downstream
            // (e.g. rerankRows) can reliably distinguish admin overrides from
            // calculated ranks.
            adminRank: (item.finalRank?.[leaderboardScoreFormat] || 0),
            rank: item.finalRank?.[leaderboardScoreFormat],
            teamRank: item.finalTeamRank?.[leaderboardScoreFormat]
        };
    });
    // console.log('Processed Leaderboard Data:', processedLeaderboardData);
    // Build a per-division map of organiser-set ranks so that rank collision
    // avoidance in the reduce below is scoped to each division independently.
    // When no divisions are in use every player lands in the '' bucket and the
    // behaviour is identical to the previous global approach.
    const divisionAdminRanksMap = new Map();
    processedLeaderboardData.forEach((item) => {
        if (item.adminRank && item.adminRank > 0) {
            const div = item.playerInfo?.[0]?.division ?? '';
            if (!divisionAdminRanksMap.has(div))
                divisionAdminRanksMap.set(div, []);
            divisionAdminRanksMap.get(div).push(item.adminRank);
        }
    });
    // Fallback global list used when a player's division has no admin ranks at all.
    const adminRanks = Array.from(processedLeaderboardData.filter((item) => item.adminRank && item.adminRank > 0).map((item) => item.adminRank));
    const sortedLeaderboardData = processedLeaderboardData
        .sort((a, b) => {
        // First, handle the case where one or both players have 0 holes completed
        if (a.holesCompleted === 0 && b.holesCompleted === 0) {
            return 0; // Both have 0 holes, keep original order
        }
        if (a.holesCompleted === 0) {
            return 1; // a has 0 holes, b has some - a goes after b
        }
        if (b.holesCompleted === 0) {
            return -1; // b has 0 holes, a has some - b goes after a
        }
        // Handle withdrawn/disqualified players - move them to bottom
        if (a.widthdrawn || a.disqualified)
            return 1;
        if (b.widthdrawn || b.disqualified)
            return -1;
        if (stableford) {
            // Stableford: higher points = better rank
            if (a.totalPoints !== b.totalPoints) {
                return b.totalPoints - a.totalPoints;
            }
        }
        else {
            // Stroke play: lower toPar = better rank
            if (a.toPar !== b.toPar) {
                return a.toPar - b.toPar;
            }
        }
        // Tiebreak: more holes completed first
        return b.holesCompleted - a.holesCompleted;
    })
        .reduce((acc, item) => {
        const itemDivision = item.playerInfo?.[0]?.division ?? '';
        // Use division-scoped admin ranks to prevent ranks from one division
        // bleeding into the collision-avoidance logic of another division.
        const divAdminRanks = divisionAdminRanksMap.get(itemDivision) ?? adminRanks;
        // All players from this division that have already been ranked.
        // Using the division-local list means indices and tie comparisons are
        // always relative to peers within the same division, not globally.
        const divisionPeersInAcc = acc.filter((e) => (e.playerInfo?.[0]?.division ?? '') === itemDivision);
        const divisionLocalIndex = divisionPeersInAcc.length; // 0-based
        // Maximum rank already assigned within this division.
        const maxExistingRank = divisionPeersInAcc.length > 0
            ? Math.max(0, ...divisionPeersInAcc.map((e) => e.rank || 0))
            : Math.max(0, ...processedLeaderboardData
                .filter((e) => (e.playerInfo?.[0]?.division ?? '') === itemDivision)
                .map((e) => e.adminRank || 0));
        // Previous player within the same division (for tie detection).
        // We deliberately compare within the division, not against
        // array[index - 1], which could be from a different division.
        const prevInDivision = divisionPeersInAcc.length > 0
            ? divisionPeersInAcc[divisionPeersInAcc.length - 1]
            : null;
        // Calculate the new rank based on sorting rules
        let calculatedRank;
        let calcRankComment;
        // If item already has an organiser-set rank, keep it
        if (item.adminRank && item.adminRank > 0) {
            calculatedRank = item.adminRank;
            calcRankComment = 'Keep existing admin rank';
        }
        // If item ties with the previous player in the same division, share rank
        else if (prevInDivision &&
            ((item.holesCompleted > 0 &&
                prevInDivision.holesCompleted > 0 &&
                (stableford
                    ? item.totalPoints === prevInDivision.totalPoints
                    : item.toPar === prevInDivision.toPar)) ||
                (item.holesCompleted === 0 && prevInDivision.holesCompleted === 0))) {
            calculatedRank = prevInDivision.rank;
            calcRankComment = 'Same rank for ties';
        }
        // Otherwise assign the next available rank using division-local position,
        // avoiding any collision with organiser-set ranks within this division.
        else {
            const divLocalPos = divisionLocalIndex + 1; // 1-based position within division
            // Start at the natural score-position rank and walk forward past any
            // slots already reserved by admin (playoff) ranks in this division.
            let candidate = divLocalPos;
            while (divAdminRanks.includes(candidate)) {
                candidate++;
            }
            calculatedRank = candidate;
            calcRankComment = 'New rank after max existing rank';
        }
        //console.log('Calculated Rank:', calculatedRank, calcRankComment);
        //console.log('Max Existing Rank:', maxExistingRank);
        //console.log('Division Admin Ranks:', divAdminRanks);
        item.rank = calculatedRank;
        if ((item.adminRank && item.adminRank > 0) || item.indPlayoffWin) {
            const divisionPeerCount = processedLeaderboardData.filter((e) => (e.playerInfo?.[0]?.division ?? '') === itemDivision).length;
            (0, seriesPointsDebug_1.logSeriesPoints)('rankAssign', {
                name: item.playerName,
                division: itemDivision,
                flight: item.playerInfo?.[0]?.flight ?? '',
                toPar: item.toPar,
                adminRank: item.adminRank,
                rank: calculatedRank,
                divisionPeerCount,
            });
        }
        acc.push(item);
        return acc;
    }, [])
        .sort((a, b) => {
        // Sort by rank value first, then by holes completed (descending)
        if (a.rank !== b.rank) {
            return a.rank - b.rank;
        }
        return b.holesCompleted - a.holesCompleted;
    });
    // console.log('Sorted Leaderboard Data:', sortedLeaderboardData);
    return sortedLeaderboardData;
};
exports.orderLeaderboardData = orderLeaderboardData;
